import React, { useEffect } from "react";
import { usePuzzleStore } from "../../game/usePuzzleStore";
import { AxisMove } from "../../game/types";
import { PuzzleGridView } from "./PuzzleGridView";
import { ClassicPuzzleView } from "./ClassicPuzzleView";
import { PuzzleHud } from "./PuzzleHud";
import { AdSlot } from "../layout/ads/AdSlot";

export const GamePanel: React.FC = () => {
  const {
    state,
    classicState,
    history,
    startedAt,
    completedAt,
    isShowingSolution,
    difficulty,
    mode,
    setMode,
    setDifficulty,
    setShowSolution,
    reset,
    nudgeAxis,
    performMove,
    swapClassicPiece,
    undo
  } = usePuzzleStore();

  useEffect(() => {
    if (!startedAt) {
      reset();
    }
  }, [startedAt, reset]);

  const handleAxisMove = (move: AxisMove) => {
    performMove(move);
  };

  const handleUndo = () => {
    undo();
  };

  const handleShowDown = () => setShowSolution(true);
  const handleShowUp = () => setShowSolution(false);

  return (
    <div className="stack-v stack-v--gap-md">
      <PuzzleHud
        startedAt={startedAt}
        completedAt={completedAt}
        moveCount={history.length}
        difficulty={difficulty}
        mode={mode}
        onReset={() => reset()}
        onDifficultyChange={setDifficulty}
        onModeChange={setMode}
      />

      {mode === "classic" ? (
        classicState ? (
          <ClassicPuzzleView
            state={classicState}
            isShowingSolution={isShowingSolution}
            completedAt={completedAt}
            onSwap={swapClassicPiece}
          />
        ) : (
          <div style={{ padding: "2rem", textAlign: "center", color: "rgba(148, 163, 184, 0.8)" }}>
            Loading puzzle...
          </div>
        )
      ) : (
        <PuzzleGridView
          state={state}
          isShowingSolution={isShowingSolution}
          completedAt={completedAt}
          onAxisMove={handleAxisMove}
          onAxisNudge={nudgeAxis}
        />
      )}

      <div
        style={{
          width: "100%",
          maxWidth: 420,
          marginInline: "auto",
          marginTop: "0.6rem",
          display: "flex",
          justifyContent: "space-between",
          gap: "0.75rem"
        }}
      >
        <button
          className="btn btn--ghost"
          type="button"
          onClick={handleUndo}
          disabled={history.length === 0 || !!completedAt}
          style={{ minWidth: 96, justifyContent: "center" }}
        >
          undo
        </button>
        <button
          className="btn btn--primary"
          type="button"
          onMouseDown={handleShowDown}
          onMouseUp={handleShowUp}
          onMouseLeave={handleShowUp}
          onTouchStart={handleShowDown}
          onTouchEnd={handleShowUp}
          style={{ minWidth: 96, justifyContent: "center" }}
        >
          show
        </button>
      </div>

      {completedAt && (
        <div className="stack-v stack-v--gap-md" style={{ width: "100%", maxWidth: 420, marginInline: "auto" }}>
          <section
            aria-label="Puzzle completion message"
            className="card"
            style={{
              padding: "0.6rem 0.8rem",
              background:
                "radial-gradient(circle at top left, rgba(34,197,94,0.18), rgba(15,23,42,0.95))",
              border: "1px solid rgba(34,197,94,0.6)"
            }}
          >
            <div className="stack-h stack-h--gap-sm" style={{ alignItems: "center" }}>
              <div
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: "999px",
                  background:
                    "conic-gradient(from 220deg, #22c55e, #22d3ee, #6366f1, #22c55e)"
                }}
              />
              <div className="stack-v stack-v--gap-xs">
                <span className="text-sm" style={{ fontWeight: 600 }}>
                  Congrats, puzzle complete!
                </span>
                <span className="text-xs text-muted">
                  Great spot for a celebratory ad or share card later.
                </span>
              </div>
            </div>
          </section>

          {/* Interstitial ad slot - shown after puzzle completion */}
          <AdSlot
            id="puzzle-completion-ad"
            size="banner"
            format="display"
            position="inline"
            label="Advertisement"
            description="Support the game by viewing an ad"
            width={320}
            height={100}
            showPlaceholder={true}
          />

          <button
            className="btn btn--primary"
            type="button"
            onClick={() => reset()}
            style={{ width: "100%" }}
          >
            New Puzzle
          </button>
        </div>
      )}
    </div>
  );
};


